import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaPrint,
    FaFileExcel,
    FaCopy,
    FaRegFileImage
} from 'react-icons/fa';

const AuditWorkOrderList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('work-order-society');
    const pageTitle = isSociety ? "मजूर संख्या वर्क ऑर्डर यादी" : "सु.बे. वर्क ऑर्डर यादी";

    const [registrations, setRegistrations] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);

        // Fetch Registrations and determine Available Dates
        const regPath = isSociety ? 'ekamwatapzp/SocietyRegistrations' : 'ekamwatapzp/EngineerRegistrations';
        const regRef = ref(db, regPath);

        onValue(regRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let datesSet = new Set();
                
                const regs = Object.entries(data).map(([key, val]) => {
                    if (val.GeneratedLottery) {
                        Object.keys(val.GeneratedLottery).forEach(dateStr => {
                            datesSet.add(dateStr);
                        });
                    }
                    return {
                        ...val,
                        _key: key
                    };
                });
                
                const dateList = Array.from(datesSet).sort().reverse().map(date => ({
                    id: date,
                    date: date
                }));
                
                setMeetings(dateList);
                setRegistrations(regs);
            } else {
                setMeetings([]);
                setRegistrations([]);
            }
            setLoading(false);
        });

    }, [isSociety]);

    const handleSearch = () => {
        setAppliedDate(selectedDate);
    };

    // Filter and Process Data
    const displayData = useMemo(() => {
        if (!appliedDate) return [];

        const results = [];
        registrations.forEach(reg => {
            if (reg.GeneratedLottery && reg.GeneratedLottery[appliedDate]) {
                const work = reg.GeneratedLottery[appliedDate];
                results.push({
                    id: work.id || work.workNumber,
                    applicantName: reg.firstName ? `${reg.firstName} ${reg.surname}` : (reg.fullName || reg.societyName || reg.name || "-"),
                    phone: reg.whatsappNo || reg.mobile || reg.phoneNumber || "-",
                    taluka: work.taluka || reg.taluka || "-",
                    workName: work.workName || "-",
                    tenderAmount: work.tenderAmount || "-",
                    fullReg: reg,
                    workData: work
                });
            }
        });

        if (searchTerm) {
            return results.filter(item => 
                item.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.workName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.taluka.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return results;
    }, [registrations, appliedDate, searchTerm]);

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#085e53] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7f6] min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <h1 className="text-xl font-bold text-gray-800 tracking-tight">{pageTitle}</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-medium text-gray-400">
                    सांगली जिल्हा परिषद / <span className="text-gray-600">{pageTitle}</span>
                </div>
            </div>

            {/* Filter Card */}
            <div className="bg-white rounded border border-gray-200 shadow-sm p-6 mb-6 no-print">
                <div className="flex flex-col gap-2">
                    <label className="text-sm text-gray-700 font-semibold mb-1">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                        <select 
                            className="w-full md:w-64 bg-white border border-[#5c6bc0]/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#5c6bc0] transition text-gray-700"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        >
                            <option value="">लॉटरीची तारीख निवडा</option>
                            {meetings.map(m => (
                                <option key={m.id} value={m.id}>{m.date}</option>
                            ))}
                        </select>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#5c6bc0] hover:bg-[#3f51b5] text-white px-6 py-2 rounded text-sm font-semibold transition"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Controls */}
            <div className="bg-white rounded-t border border-gray-200 p-4 border-b-0 flex flex-col md:flex-row justify-between items-center gap-4 no-print">
                <div className="flex items-center gap-0">
                    <button className="bg-[#6c757d] border-r border-gray-500 text-white px-4 py-1.5 rounded-l text-sm hover:bg-gray-700 transition">Copy</button>
                    <button className="bg-[#6c757d] border-r border-gray-500 text-white px-4 py-1.5 text-sm hover:bg-gray-700 transition">Excel</button>
                    <button onClick={() => window.print()} className="bg-[#6c757d] text-white px-4 py-1.5 rounded-r text-sm hover:bg-gray-700 transition">Print</button>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Search:</span>
                    <input 
                        type="text"
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-400 w-48"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Main Table */}
            <div className="bg-white rounded-b border border-gray-200 overflow-hidden mb-8 shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="border-b border-gray-200">
                            <tr className="text-[13px] font-semibold text-gray-800 bg-white">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        # <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[200px]">
                                    <div className="flex items-center justify-between">
                                        अर्जाचे नाव <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[120px] whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        फोन नंबर <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[100px] whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        तालुका <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[300px]">
                                    <div className="flex items-center justify-between">
                                        कामाचे नाव <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 text-center w-24 whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        वर्क ऑर्डर <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-center w-24 whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        करार नामा <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {displayData.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition-colors text-sm text-gray-700">
                                    <td className="px-4 py-4 border-r border-gray-200">{index + 1}</td>
                                    <td className="px-4 py-4 border-r border-gray-200 leading-relaxed">
                                        <div className="font-bold text-gray-900">{item.applicantName}</div>
                                    </td>
                                    <td className="px-4 py-4 border-r border-gray-200">{item.phone}</td>
                                    <td className="px-4 py-4 border-r border-gray-200">{item.taluka}</td>
                                    <td className="px-4 py-4 border-r border-gray-200 leading-relaxed">
                                        <div className="font-bold text-gray-900 mb-1">{item.workName}</div>
                                        <div className="text-[12px] text-gray-600">Rs. - {item.tenderAmount}</div>
                                    </td>
                                    <td className="px-4 py-4 border-r border-gray-200 text-center">
                                        <div className="flex justify-center gap-3">
                                            <button 
                                                onClick={() => navigate('/audit/reports/update-work-order', { state: { regData: item, workData: item } })}
                                                className="text-[#3b82f6] hover:text-blue-800 transition p-1 bg-transparent"
                                                title="वर्क ऑर्डर अपडेट करा"
                                            >
                                                <FaRegFileImage size={14} />
                                            </button>
                                            <button 
                                                onClick={() => navigate('/audit/reports/work-order-view', { state: { regData: item, workData: item } })}
                                                className="text-[#3b82f6] hover:text-blue-800 transition p-1 bg-transparent"
                                            >
                                                <FaPrint size={14} />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <button className="text-[#3b82f6] hover:text-blue-800 transition p-2 bg-transparent">
                                            <FaPrint size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500 bg-gray-50/50">
                                        No data available in table
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Pagination */}
                <div className="p-4 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-600 no-print">
                    <div>
                        Showing {displayData.length > 0 ? 1 : 0} to {displayData.length} of {displayData.length} entries
                    </div>
                    <div className="flex items-center border border-gray-300 rounded text-sm overflow-hidden">
                        <button className="px-3 py-1 text-gray-500 hover:bg-gray-50 border-r border-gray-300 cursor-pointer bg-white">Previous</button>
                        <button className="px-3 py-1 bg-[#5c6bc0] text-white border-r border-gray-300">1</button>
                        <button className="px-3 py-1 text-gray-600 hover:bg-gray-50 cursor-pointer bg-white">Next</button>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                    .bg-white { box-shadow: none !important; border: none !important; }
                    table { border: 1px solid #ddd !important; border-collapse: collapse !important; width: 100% !important; }
                    th, td { border: 1px solid #ddd !important; padding: 12px !important; }
                }
            `}</style>
        </div>
    );
};

export default AuditWorkOrderList;
